import { type FC } from 'react';

import { type ActivityStatus } from 'connect-graphql/generated';

import Avatar from '@mui/material/Avatar';

import { AvatarDiscreteSize, getAvatarSize } from '@craft/ui/components/avatars';
import { quantitativeSizeToString } from '@craft/ui/util';

import {
  getActivityStatusBackgroundStatusColor,
  getActivityStatusColor,
  getActivityStatusIcon,
  isActivityOverdue,
} from '@/src/model';

export interface ActivityStatusAvatarProps {
  readonly dueDate: Date | null | undefined;
  readonly size?: Extract<AvatarDiscreteSize, AvatarDiscreteSize.Large | AvatarDiscreteSize.Small>;
  readonly status: ActivityStatus;
}

/**
 * Displays the status of an activity, {@link ActivityStatus}.
 *
 * @see https://www.figma.com/file/kZ6spnfsAZYyn90Ovub3LV/Craft-Component-Library?type=design&node-id=35-12&mode=design&t=gfproB2APRsIv2nR-0
 *
 * @deprecated
 * Please leverage the IconAvatar component in @craft/ui to define the variants for each activity
 * status in a new component in src/features instead.
 */
export const ActivityStatusAvatar: FC<ActivityStatusAvatarProps> = ({
  dueDate,
  size = AvatarDiscreteSize.Large,
  status,
}) => {
  const isOverdue = isActivityOverdue(dueDate, status);
  const StatusIcon = getActivityStatusIcon(status, isOverdue);
  return (
    <Avatar
      sx={{
        bgcolor: getActivityStatusBackgroundStatusColor(status, isOverdue),
        borderColor: getActivityStatusColor(status, isOverdue),
        borderStyle: 'solid',
        borderWidth: '1px',
        color: getActivityStatusColor(status, isOverdue),
        height: quantitativeSizeToString(getAvatarSize(size), 'px'),
        width: quantitativeSizeToString(getAvatarSize(size), 'px'),
      }}
    >
      <StatusIcon sx={{ fontSize: size === AvatarDiscreteSize.Large ? '1.5rem' : '0.9rem' }} />
    </Avatar>
  );
};
